/**
 * FrameworkCards
 * --------------
 * Interactive presentation of the Resonow Stage + Domain framework.
 * Renders inside the Wellness Concierge chat when the AI emits the
 * [framework_cards] marker. Canonical stage wording is read from i18n
 * (therapyLibrary.stages.*) so it stays in sync with the /framework page.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Heart, Sparkles, Search, Compass, Mountain,
  Activity, Flame, Brain, Globe2, ChevronDown, ChevronRight,
  ArrowRight, Target,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FrameworkCardsProps {
  onChipClick?: (prompt: string) => void;
}

const STAGE_ICONS = [Heart, Sparkles, Search, Compass, Mountain];

const DOMAINS_EN = [
  { key: 'body', label: 'Body', icon: Activity, desc: 'Physiological regulation: sleep, hormones, nutrition, movement, nervous system' },
  { key: 'vitality', label: 'Vitality', icon: Flame, desc: 'Emotional energy, regulation capacity, motivation, burnout' },
  { key: 'cognition', label: 'Cognition', icon: Brain, desc: 'Beliefs, self-narratives, interpretation, meaning-making' },
  { key: 'context', label: 'Context', icon: Globe2, desc: 'Relationships, work, family, culture, environment, lifestyle' },
];

const DOMAINS_ZH = [
  { key: 'body', label: '身 · Body', icon: Activity, desc: '生理调节：睡眠、激素、营养、运动、神经系统' },
  { key: 'vitality', label: '灵 · Vitality', icon: Flame, desc: '情绪能量、调节能力、动力、倦怠' },
  { key: 'cognition', label: '意 · Cognition', icon: Brain, desc: '信念、自我叙事、解释方式、意义建构' },
  { key: 'context', label: '境 · Context', icon: Globe2, desc: '关系、工作、家庭、文化、环境、生活方式' },
];

const FrameworkCards: React.FC<FrameworkCardsProps> = ({ onChipClick }) => {
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language?.startsWith('zh');
  const [stagesOpen, setStagesOpen] = useState(true);
  const [domainsOpen, setDomainsOpen] = useState(true);
  const [taggingOpen, setTaggingOpen] = useState(false);

  const domains = isZh ? DOMAINS_ZH : DOMAINS_EN;

  const stages = [0, 1, 2, 3, 4].map((n) => ({
    n,
    title: t(`stages.${n}.title`),
    description: t(`stages.${n}.description`),
    Icon: STAGE_ICONS[n],
  }));

  const chips = isZh
    ? [
        { label: '🧭 探索五个阶段', prompt: '我想多了解 5 个阶段' },
        { label: '🌿 探索四大领域', prompt: '我想多了解 4 个领域' },
        { label: '🎯 帮我找到我的阶段', prompt: '帮我找到我现在处于哪个阶段' },
        { label: '✨ 帮我找到我的领域', prompt: '帮我看看我主要在哪些领域' },
      ]
    : [
        { label: '🧭 Explore Stages', prompt: 'Tell me more about the 5 stages' },
        { label: '🌿 Explore Domains', prompt: 'Tell me more about the 4 domains' },
        { label: '🎯 Find my Stage', prompt: 'Help me figure out which stage I am in' },
        { label: '✨ Find my Domain', prompt: 'Help me figure out which domains apply to me' },
      ];

  return (
    <div className="not-prose my-3 space-y-3">
      {/* Stages section */}
      <section className="rounded-xl border border-warm-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setStagesOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-warm-50/60 hover:bg-warm-100/60 transition-colors"
        >
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-700">
            {isZh ? '五个阶段' : 'The 5 Stages'}
          </span>
          {stagesOpen ? (
            <ChevronDown className="h-4 w-4 text-gold-ink" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gold-ink" />
          )}
        </button>
        {stagesOpen && (
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {stages.map(({ n, title, description, Icon }) => (
              <div
                key={n}
                className="group rounded-lg border border-warm-100 bg-cream/40 hover:border-brand-300 hover:bg-cream/70 transition-colors p-2.5 flex gap-2.5"
              >
                <div className="shrink-0 w-8 h-8 rounded-full bg-brand-50 text-gold-ink flex items-center justify-center text-[11px] font-semibold">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Stage {n}
                  </div>
                  <div className="text-sm font-medium text-foreground leading-snug">
                    {title}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Domains section */}
      <section className="rounded-xl border border-warm-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setDomainsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-warm-50/60 hover:bg-warm-100/60 transition-colors"
        >
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-700">
            {isZh ? '四大领域' : 'The 4 Domains'}
          </span>
          {domainsOpen ? (
            <ChevronDown className="h-4 w-4 text-gold-ink" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gold-ink" />
          )}
        </button>
        {domainsOpen && (
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {domains.map(({ key, label, desc, icon: Icon }) => (
              <div
                key={key}
                className="rounded-lg border border-warm-100 bg-cream/40 hover:border-brand-300 hover:bg-cream/70 transition-colors p-2.5 flex gap-2.5"
              >
                <div className="shrink-0 w-8 h-8 rounded-full bg-brand-50 text-gold-ink flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground leading-snug">
                    {label}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Practitioner tagging — expandable note */}
      <section className="rounded-xl border border-warm-200 bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setTaggingOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-warm-50/60 hover:bg-warm-100/60 transition-colors"
        >
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-700 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" />
            {isZh ? '疗愈师标签' : 'Practitioner Tagging'}
          </span>
          {taggingOpen ? (
            <ChevronDown className="h-4 w-4 text-gold-ink" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gold-ink" />
          )}
        </button>
        {taggingOpen && (
          <p className="px-3 pb-3 pt-1 text-xs text-muted-foreground leading-relaxed">
            {isZh
              ? 'Resonow 上的每位疗愈师都按其支持的阶段（Stages）与擅长的领域（Domains）打上标签，让客户能根据"自己现在真正在哪里"被精准匹配，而不是仅凭症状标签。'
              : 'Every practitioner on Resonow is tagged by the Stages they support and the Domains they primarily work in, so clients are matched to practitioners whose scope fits where they actually are — not by symptom labels alone.'}
          </p>
        )}
      </section>

      {/* Guided exploration chips */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => onChipClick?.(c.prompt)}
            className="text-xs px-3 py-1.5 rounded-full border border-brand-200 bg-cream hover:bg-brand-50 hover:border-brand-400 text-brand-700 transition-colors"
          >
            {c.label}
          </button>
        ))}
        <Link
          to="/framework"
          className="text-xs px-3 py-1.5 rounded-full border border-warm-200 bg-white hover:bg-warm-50 text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
        >
          {isZh ? '打开完整架构页面' : 'Open full Framework page'}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
};

export default FrameworkCards;
